import React, { useMemo } from 'react';
import { theme } from '../../../theme';
import { FixedBooking, GridVenue, BOOKING_COLORS, DAY_OF_WEEK_SHORT } from '../../../types/venue';
import { User } from 'lucide-react';

interface FixedBookingWeeklyGridProps {
  fixedBookings: FixedBooking[];
  venues: GridVenue[];
  selectedVenueId?: string;
  onFixedBookingClick: (fixedBooking: FixedBooking) => void;
}

const START_HOUR = 8;
const END_HOUR = 24;

// Day order for columns: Mon(1) Tue(2) Wed(3) Thu(4) Fri(5) Sat(6) Sun(0)
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const FixedBookingWeeklyGrid: React.FC<FixedBookingWeeklyGridProps> = ({
  fixedBookings,
  venues,
  selectedVenueId,
  onFixedBookingClick,
}) => {
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = START_HOUR; hour < END_HOUR; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  }, []);

  const filteredBookings = useMemo(() => {
    let filtered = fixedBookings.filter(fb => fb.status === 'ACTIVE' || fb.status === 'PAUSED');
    if (selectedVenueId) {
      filtered = filtered.filter(fb => fb.venueId === selectedVenueId);
    }
    return filtered;
  }, [fixedBookings, selectedVenueId]);

  // Build a lookup: [dayOfWeek][time] -> FixedBooking[]
  const bookingMap = useMemo(() => {
    const map: Record<number, Record<string, FixedBooking[]>> = {};
    DAY_ORDER.forEach(day => {
      map[day] = {};
      timeSlots.forEach(time => {
        map[day][time] = [];
      });
    });

    filteredBookings.forEach(fb => {
      timeSlots.forEach(time => {
        if (fb.startTime <= time && fb.endTime > time) {
          if (map[fb.dayOfWeek]?.[time]) {
            map[fb.dayOfWeek][time].push(fb);
          }
        }
      });
    });

    return map;
  }, [filteredBookings, timeSlots]);

  const getVenueName = (venueId: string) => {
    const venue = venues.find(v => v.id === venueId);
    return venue ? venue.name : '';
  };

  // Assign colors per venue for multi-venue view
  const venueColors = useMemo(() => {
    const colors: Record<string, string> = {};
    const palette = [
      BOOKING_COLORS.FIXED,
      '#7E57C2', // purple
      '#26A69A', // teal
      '#EF5350', // red
      '#FFA726', // orange
      '#66BB6A', // green
      '#AB47BC', // deep purple
      '#29B6F6', // light blue
    ];
    venues.forEach((v, i) => {
      colors[v.id] = palette[i % palette.length];
    });
    return colors;
  }, [venues]);

  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
  };

  const gridContainerStyle: React.CSSProperties = {
    display: 'flex',
    position: 'relative',
    maxHeight: 'calc(100vh - 340px)',
    overflowY: 'auto',
  };

  const timeColumnStyle: React.CSSProperties = {
    minWidth: '60px',
    width: '60px',
    backgroundColor: theme.colors.background,
    borderRight: `1px solid ${theme.colors.border}`,
    flexShrink: 0,
    position: 'sticky',
    left: 0,
    zIndex: 2,
  };

  const timeSlotStyle: React.CSSProperties = {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: theme.colors.textSecondary,
    borderBottom: `1px solid ${theme.colors.border}`,
    fontWeight: theme.fonts.weightBold,
  };

  const gridBodyStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
  };

  const columnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: '100px',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: theme.colors.background,
    borderBottom: `2px solid #D4AF37`,
    position: 'sticky',
    top: 0,
    zIndex: 3,
  };

  const headerCellStyle: React.CSSProperties = {
    flex: 1,
    minWidth: '100px',
    padding: '12px 8px',
    textAlign: 'center',
    borderRight: `1px solid ${theme.colors.border}`,
  };

  const headerLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const timeColumnHeaderStyle: React.CSSProperties = {
    minWidth: '60px',
    width: '60px',
    padding: '12px 8px',
    textAlign: 'center',
    backgroundColor: theme.colors.background,
    borderRight: `1px solid ${theme.colors.border}`,
    position: 'sticky',
    left: 0,
    zIndex: 4,
  };

  const cellStyle: React.CSSProperties = {
    height: '48px',
    minHeight: '48px',
    border: `1px solid ${theme.colors.border}`,
    position: 'relative',
    overflow: 'hidden',
  };

  const legendStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    padding: '12px',
    borderTop: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    flexWrap: 'wrap',
  };

  const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const renderCell = (dayOfWeek: number, time: string) => {
    const cellBookings = bookingMap[dayOfWeek]?.[time] || [];

    if (cellBookings.length === 0) {
      return <div style={cellStyle} />;
    }

    // If multiple bookings in same slot (different venues), stack them
    return (
      <div style={cellStyle}>
        <div style={{ display: 'flex', height: '100%', gap: '1px', padding: '1px 2px' }}>
          {cellBookings.map(fb => {
            const color = selectedVenueId ? BOOKING_COLORS.FIXED : venueColors[fb.venueId] || BOOKING_COLORS.FIXED;
            const isStartSlot = fb.startTime === time;

            return (
              <div
                key={fb.id}
                onClick={() => onFixedBookingClick(fb)}
                style={{
                  flex: 1,
                  backgroundColor: color,
                  borderRadius: isStartSlot ? '6px 6px 0 0' : '0',
                  padding: '2px 4px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  transition: 'filter 0.15s',
                  opacity: fb.status === 'PAUSED' ? 0.5 : 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
                title={`${fb.customerName} - ${fb.startTime} a ${fb.endTime}${!selectedVenueId ? ` (${getVenueName(fb.venueId)})` : ''}${fb.status === 'PAUSED' ? ' [PAUSADO]' : ''}`}
              >
                {isStartSlot && (
                  <>
                    <div style={{
                      fontSize: '10px',
                      fontWeight: theme.fonts.weightBold,
                      color: 'rgba(0,0,0,0.7)',
                      marginBottom: '1px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <span>{fb.startTime}-{fb.endTime}</span>
                      {fb.status === 'PAUSED' && (
                        <span style={{
                          fontSize: '8px',
                          backgroundColor: 'rgba(255,255,255,0.4)',
                          padding: '0 3px',
                          borderRadius: '2px',
                        }}>
                          PAUSA
                        </span>
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      fontSize: '11px',
                      fontWeight: theme.fonts.weightBold,
                      color: '#000',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      <User size={10} />
                      <span>{fb.customerName}</span>
                    </div>
                  </>
                )}
                {!isStartSlot && !selectedVenueId && (
                  <div style={{
                    fontSize: '9px',
                    color: 'rgba(0,0,0,0.5)',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {getVenueName(fb.venueId)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={timeColumnHeaderStyle}>
          <span style={{ fontSize: '11px', color: theme.colors.textSecondary }}>Hora</span>
        </div>
        {DAY_ORDER.map(day => (
          <div key={day} style={headerCellStyle}>
            <div style={headerLabelStyle}>{DAY_OF_WEEK_SHORT[day]}</div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={gridContainerStyle}>
        {/* Time column */}
        <div style={timeColumnStyle}>
          {timeSlots.map(time => (
            <div key={time} style={timeSlotStyle}>
              {time}
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div style={gridBodyStyle}>
          {DAY_ORDER.map(day => (
            <div key={day} style={columnStyle}>
              {timeSlots.map(time => (
                <React.Fragment key={`${day}-${time}`}>
                  {renderCell(day, time)}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      {!selectedVenueId && venues.length > 1 && (
        <div style={legendStyle}>
          {venues.map(v => (
            <div key={v.id} style={legendItemStyle}>
              <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '3px',
                backgroundColor: venueColors[v.id] || BOOKING_COLORS.FIXED,
              }} />
              <span>{v.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FixedBookingWeeklyGrid;
